import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { dismissedUntil, dismissalKey, extensionPreviewBanner, selectTopBanner, staleExtensionBanner, type BannerDefinition } from './bannerPolicy';
import styles from './BannerQueue.module.css';

export function BannerQueue({ banners = [] }: { banners?: readonly BannerDefinition[] }) {
  const { extensionVersion } = useAuth();
  const [, setRevision] = useState(0);
  const stale = staleExtensionBanner(extensionVersion);
  const preview = extensionPreviewBanner(extensionVersion);
  const candidates = [...banners, ...(stale ? [stale] : []), ...(preview ? [preview] : [])];
  const now = Date.now();
  const expirations = candidates.map((banner) => dismissedUntil(banner, localStorage)).filter((value): value is number => value !== null && value > now);
  const nextExpiration = expirations.length > 0 ? Math.min(...expirations) : null;
  useEffect(() => {
    if (nextExpiration === null) return;
    const timer = window.setTimeout(() => setRevision((value) => value + 1), Math.max(0, nextExpiration - Date.now()));
    return () => window.clearTimeout(timer);
  }, [nextExpiration]);
  const active = selectTopBanner(candidates.filter((banner) => {
    const until = dismissedUntil(banner, localStorage);
    return until === null || now >= until;
  }));
  if (!active) return null;
  const dismiss = () => { if (!active.dismissible) return; try { localStorage.setItem(dismissalKey(active.id), String(Date.now())); } catch { /* unavailable */ } setRevision((v) => v + 1); };
  const label = `${active.learnMoreLabel ?? 'Why does this matter?'} →`;
  // A page of the app opens in the app; anything else is a plain link.
  const learnMore = !active.learnMoreHref ? null
    : active.learnMoreHref.startsWith('/') ? <Link to={active.learnMoreHref}>{label}</Link>
      : <a href={active.learnMoreHref}>{label}</a>;
  return <section className={`${styles.banner} ${styles[active.tone]}`} role={active.tone === 'critical' ? 'alert' : 'status'} aria-live="polite">
    <span>{active.message}</span>{learnMore}
    {active.dismissible ? <button type="button" onClick={dismiss} aria-label="Dismiss notification">×</button> : null}
  </section>;
}
